import { ref, computed, provide } from 'vue';
import { injectTypes } from '@/Types/InjectTypes';

const isPromise = (value) => typeof value?.then === 'function';

export const useViewProcessing = () => {
    const isProcessingViewState = ref(false);
    // Exposed read-only so it can only change through the methods below, never directly.
    const isProcessingView = computed(() => isProcessingViewState.value);

    const startViewProcessing = () => {
        isProcessingViewState.value = true;
        return Promise.resolve(isProcessingViewState.value);
    };

    const stopViewProcessing = () => {
        isProcessingViewState.value = false;
        return Promise.resolve(isProcessingViewState.value);
    };

    const withViewProcessing = (task) => (...args) => {
        startViewProcessing();
        try {
            const result = task(...args);

            if (!isPromise(result)) {
                throw new Error('useViewProcessing: withViewProcessing expects the task to return a Promise.');
            }

            return result.finally(() => stopViewProcessing());
        }
        catch (error) {
            // Stop processing, then let the error propagate (non-Promise contract error or a sync throw).
            stopViewProcessing();
            throw error;
        }
    };

    provide(injectTypes.startViewProcessing, startViewProcessing);
    provide(injectTypes.stopViewProcessing, stopViewProcessing);
    // Reactive read-only computed; Options API injectors read it via `.value`.
    provide(injectTypes.isProcessingView, isProcessingView);
    provide(injectTypes.withViewProcessing, withViewProcessing);

    return {
        isProcessingView,
        startViewProcessing,
        stopViewProcessing,
        withViewProcessing,
    };
};
